import {
    Alert,
    ColumnLayout,
    FormField,
    Select,
    SpaceBetween,
} from "@cloudscape-design/components";
import React, { useEffect } from "react";
import { AppStreamSdkProps } from "@customTypes/toolbar";
import { Trans, useTranslation } from "react-i18next";
import useMicrophoneDevices from "@components/toolbar/useMicrophoneDevices";
import { useMediaDeviceStore } from "@components/toolbar/useMediaDeviceStore";
import useCameraDevices from "@components/toolbar/useCameraDevices";
import { useToolbarPreferenceStore } from "@hooks/useToolbarPreferenceStore";
import { EmbedConstants as AppStreamEmbedConstant } from "@amzn/photon-portal-embed-sdk";
import { getLogger } from "@utils/ermineLoggerUtils";
import { useFloatingAndToolbarNotification } from "@hooks/useNotification";
import {
    useNotificationStore,
    useNotificationToolbarItemStore,
} from "@hooks/useNotificationStore";
import {
    showMicrophoneMenu,
    showWebcamMenu,
} from "@utils/toolbarSettingsUtils";
import { NotificationId } from "@constants/toolbar";
import { ToolbarMetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

export const MicrophoneSelector = ({ appStreamSdk }: AppStreamSdkProps) => {
    const { t } = useTranslation();
    const log = useGetLogger();

    const isMicrophonePermissionAllowed = useMediaDeviceStore(
        (state) => state.isMicrophonePermissionAllowed
    );
    const setIsMicrophonePermissionAllowed = useMediaDeviceStore(
        (state) => state.setIsMicrophonePermissionAllowed
    );

    useEffect(() => {
        void checkMicrophonePermission();
    }, []);

    const checkMicrophonePermission = async () => {
        try {
            const result = await navigator.permissions.query({
                name: "microphone" as PermissionName,
            });
            log.publishCounterMetric(
                ToolbarMetricName.GET_BROWSER_PERMISSIONS_SUCCESS
            );
            setIsMicrophonePermissionAllowed(result.state === "granted");

            // Listen for changes in permission state
            result.onchange = () => {
                setIsMicrophonePermissionAllowed(result.state === "granted");
            };
        } catch (error) {
            console.error("Error checking microphone permission:", error);
            log.publishCounterMetric(
                ToolbarMetricName.GET_MICROPHONE_BROWSER_PERMISSION_ERROR
            );
        }
    };

    const microphonePermissionNotAllowedNotification = (
        <Alert
            statusIconAriaLabel="Warning"
            type="warning"
            data-testId="microphonePermissionAlert"
            header={t(
                "toolbar.item.preference.content.tab.audioVideo.microphone.permissionAlert.header"
            )}
        >
            <Trans
                i18nKey={
                    "toolbar.item.preference.content.tab.audioVideo.microphone.permissionAlert.content"
                }
            />
        </Alert>
    );

    const microphones = useMicrophoneDevices(appStreamSdk);
    const microphoneOptions = microphones.map((option) => {
        return { label: option.label, value: option.deviceId };
    });

    const selectedMicrophone = useMediaDeviceStore(
        (state) => state.selectedMicrophone
    );
    const setSelectedMicrophone = useMediaDeviceStore(
        (state) => state.setSelectedMicrophone
    );

    const microphoneEnabled = useToolbarPreferenceStore(
        (store) => store.microphoneEnabled
    );

    const addFloatingNotification = useNotificationStore(
        (store) => store.addNotification
    );

    const addToolbarNotification = useNotificationToolbarItemStore(
        (store) => store.addNotification
    );

    const microphoneNotEnabledError = useFloatingAndToolbarNotification({
        header: t("notification.microphoneNotEnabled.header"),
        content: t("notification.microphoneNotEnabled.content"),
        notificationId: NotificationId.MICROPHONE_NOT_ENABLED_ERROR,
    });

    const [selectedOption, setSelectedOption] = React.useState({
        label: selectedMicrophone ? selectedMicrophone.label : undefined,
        value: selectedMicrophone ? selectedMicrophone.deviceId : undefined,
    });

    return (
        <FormField
            label={t(
                "toolbar.item.preference.content.tab.audioVideo.microphone.label"
            )}
            description={t(
                "toolbar.item.preference.content.tab.audioVideo.microphone.description"
            )}
            data-testid="microphoneFormField"
        >
            {isMicrophonePermissionAllowed ? (
                <Select
                    selectedOption={selectedOption}
                    onChange={async ({ detail }) => {
                        setSelectedOption({
                            label: detail.selectedOption.label,
                            value: detail.selectedOption.value,
                        });
                        if (microphoneEnabled) {
                            try {
                                await appStreamSdk.performAction(
                                    AppStreamEmbedConstant.METHOD_SET_MICROPHONE,
                                    {
                                        microphoneEnabled: microphoneEnabled,
                                        microphoneDeviceId:
                                            detail.selectedOption.value,
                                    }
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.ENABLE_MICROPHONE_EXPANDED_VIEW_SUCCESS
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.SELECT_MICROPHONE_DEVICE_SUCCESS
                                );
                                setSelectedMicrophone({
                                    label: detail.selectedOption.label,
                                    deviceId: detail.selectedOption.value,
                                });
                            } catch {
                                addFloatingNotification(
                                    microphoneNotEnabledError.floatingToolbarNotification
                                );
                                addToolbarNotification(
                                    microphoneNotEnabledError.toolbarNotification
                                );
                                getLogger().publishCounterMetric(
                                    ToolbarMetricName.ENABLE_MICROPHONE_ERROR
                                );
                                getLogger().publishCounterMetric(
                                    ToolbarMetricName.SELECT_MICROPHONE_DEVICE_ERROR
                                );
                            }
                        } else {
                            setSelectedMicrophone({
                                label: detail.selectedOption.label,
                                deviceId: detail.selectedOption.value,
                            });
                        }
                    }}
                    options={microphoneOptions}
                    placeholder={t(
                        "toolbar.item.preference.content.tab.audioVideo.microphone.placeholder"
                    )}
                    data-testid="microphoneSelectMenu"
                />
            ) : (
                microphonePermissionNotAllowedNotification
            )}
        </FormField>
    );
};

export const CameraSelector = ({ appStreamSdk }: AppStreamSdkProps) => {
    const { t } = useTranslation();
    const log = useGetLogger();

    const isWebcamPermissionAllowed = useMediaDeviceStore(
        (state) => state.isWebcamPermissionAllowed
    );
    const setIsWebcamPermissionAllowed = useMediaDeviceStore(
        (state) => state.setIsWebcamPermissionAllowed
    );

    useEffect(() => {
        void checkCameraPermission();
    }, []);

    const checkCameraPermission = async () => {
        try {
            const result = await navigator.permissions.query({
                name: "camera" as PermissionName,
            });
            setIsWebcamPermissionAllowed(result.state === "granted");
            log.publishCounterMetric(
                ToolbarMetricName.GET_BROWSER_PERMISSIONS_SUCCESS
            );

            // Listen for changes in permission state
            result.onchange = () => {
                setIsWebcamPermissionAllowed(result.state === "granted");
            };
        } catch (error) {
            console.error("Error checking camera permission:", error);
            log.publishCounterMetric(
                ToolbarMetricName.GET_WEBCAM_BROWSER_PERMISSION_ERROR
            );
        }
    };

    const cameraPermissionNotAllowedNotification = (
        <Alert
            statusIconAriaLabel="Warning"
            type="warning"
            data-testId="cameraPermissionAlert"
            header={t(
                "toolbar.item.preference.content.tab.audioVideo.camera.permissionAlert.header"
            )}
        >
            <Trans
                i18nKey={
                    "toolbar.item.preference.content.tab.audioVideo.camera.permissionAlert.content"
                }
            />
        </Alert>
    );

    const addFloatingNotification = useNotificationStore(
        (store) => store.addNotification
    );

    const addToolbarNotification = useNotificationToolbarItemStore(
        (store) => store.addNotification
    );

    const cameraNotEnabledError = useFloatingAndToolbarNotification({
        header: t("notification.cameraNotEnabled.header"),
        content: t("notification.cameraNotEnabled.content"),
        notificationId: NotificationId.CAMERA_NOT_ENABLED_ERROR,
    });

    const cameraDevices = useCameraDevices(appStreamSdk);
    const cameraOptions = (cameraDevices || []).map((option) => {
        return { label: option.label, value: option.deviceId };
    });

    const selectedWebcam = useMediaDeviceStore((state) => state.selectedWebcam);
    const setSelectedWebcam = useMediaDeviceStore(
        (state) => state.setSelectedWebcam
    );
    const webcamEnabled = useToolbarPreferenceStore(
        (store) => store.webcamEnabled
    );

    const [selectedOption, setSelectedOption] = React.useState({
        label: selectedWebcam ? selectedWebcam.label : undefined,
        value: selectedWebcam ? selectedWebcam.deviceId : undefined,
    });

    return (
        <FormField
            label={t(
                "toolbar.item.preference.content.tab.audioVideo.camera.label"
            )}
            description={t(
                "toolbar.item.preference.content.tab.audioVideo.camera.description"
            )}
            data-testid="cameraFormField"
        >
            {isWebcamPermissionAllowed ? (
                <Select
                    selectedOption={selectedOption}
                    onChange={async ({ detail }) => {
                        setSelectedOption({
                            label: detail.selectedOption.label,
                            value: detail.selectedOption.value,
                        });
                        if (webcamEnabled) {
                            try {
                                // first turn off the previous selected camera
                                await appStreamSdk.performAction(
                                    AppStreamEmbedConstant.METHOD_SET_WEBCAM,
                                    {
                                        webcamEnabled: false,
                                    }
                                );
                                // then enable the selected camera
                                await appStreamSdk.performAction(
                                    AppStreamEmbedConstant.METHOD_SET_WEBCAM,
                                    {
                                        webcamEnabled: true,
                                        webcamDeviceId:
                                            detail.selectedOption.value,
                                    }
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.ENABLE_WEBCAM_EXPANDED_VIEW_SUCCESS
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.SELECT_WEBCAM_DEVICE_SUCCESS
                                );
                                setSelectedWebcam({
                                    label: detail.selectedOption.label,
                                    deviceId: detail.selectedOption.value,
                                });
                            } catch {
                                addFloatingNotification(
                                    cameraNotEnabledError.floatingToolbarNotification
                                );
                                addToolbarNotification(
                                    cameraNotEnabledError.toolbarNotification
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.ENABLE_WEBCAM_ERROR
                                );
                                log.publishCounterMetric(
                                    ToolbarMetricName.SELECT_WEBCAM_DEVICE_ERROR
                                );
                            }
                        } else {
                            setSelectedWebcam({
                                label: detail.selectedOption.label,
                                deviceId: detail.selectedOption.value,
                            });
                        }
                    }}
                    options={cameraOptions}
                    placeholder={t(
                        "toolbar.item.preference.content.tab.audioVideo.camera.placeholder"
                    )}
                    data-testid="cameraSelectMenu"
                />
            ) : (
                cameraPermissionNotAllowedNotification
            )}
        </FormField>
    );
};

/**
 * Preferences - Audio & Video tab content
 */
export const AudioVideoTabContent = ({ appStreamSdk }: AppStreamSdkProps) => {
    return (
        <ColumnLayout columns={2} data-testId="audio-video-tab-content">
            {showMicrophoneMenu() && (
                <SpaceBetween direction="vertical" size="m">
                    <MicrophoneSelector
                        appStreamSdk={appStreamSdk}
                        data-testId="microphone-selector-audioVideoTab"
                    />
                </SpaceBetween>
            )}
            {showWebcamMenu() && (
                <SpaceBetween direction="vertical" size="m">
                    <CameraSelector
                        appStreamSdk={appStreamSdk}
                        data-testId="camera-selector-audioVideoTab"
                    />
                </SpaceBetween>
            )}
        </ColumnLayout>
    );
};
